import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { getToken } from "@/lib/authToken";
import type { ShippingOrder, ShippingStatus, ShippingFilter, ShippingStats } from "@/types/shipping";
import { useUser } from "@/context/UserContext";
import CreateOrderModal from "./components/CreateOrderModal";
import ExcelImportModal from "./components/ExcelImportModal";

const statusTabs: Array<{ value: ShippingStatus | "all"; label: string }> = [
  { value: "all", label: "全部" },
  { value: "海運中", label: "海運中" },
  { value: "已到港", label: "已到港" },
];

const PAGE_SIZE = 10;

interface EmployeeOption {
  id: string;
  name: string;
  email: string;
}

function getStatusColor(status: ShippingStatus): string {
  switch (status) {
    case "海運中": return "bg-secondary-100 text-secondary-700";
    case "已到港": return "bg-primary-100 text-primary-700";
    case "已轉帳務": return "bg-foreground-100 text-foreground-700";
    default: return "bg-background-100 text-foreground-600";
  }
}

function mapRowToOrder(row: Record<string, unknown>): ShippingOrder {
  return {
    id: row.id as string,
    orderNumber: row.order_number as string,
    customer: row.customer as string,
    customerType: (row.customer_type as string) || "",
    product: row.product as string,
    quantity: (row.quantity as number) || 1,
    unit: (row.unit as string) || "瓶",
    logistics: (row.logistics as string) || "",
    trackingNumber: (row.tracking_number as string) || "",
    expectedArrival: row.expected_arrival as string,
    deliveryDeadline: row.delivery_deadline as string,
    amount: Number(row.amount) || 0,
    currency: (row.currency as string) || "NT$",
    status: row.status as ShippingStatus,
    employeeId: row.employee_id as string,
    createdAt: row.created_at as string,
  };
}

export default function ShippingPage() {
  const user = useUser();
  const [filter, setFilter] = useState<ShippingFilter>({
    search: "",
    status: "all",
    employeeId: user.isAdmin ? "all" : (user.employeeId || ""),
  });
  const [orders, setOrders] = useState<ShippingOrder[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const showToast = useCallback((message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Fetch employees list (for sandbox selector)
  useEffect(() => {
    supabase
      .from("employees")
      .select("id, name, email")
      .order("name")
      .then(({ data, error }) => {
        if (!error && data) {
          setEmployees(data as EmployeeOption[]);
        }
      });
  }, []);

  // One-time seed for existing employees with 0 orders
  const hasSeededRef = useRef(false);
  useEffect(() => {
    if (!user.employeeId || hasSeededRef.current) return;
    hasSeededRef.current = true;
    const seedIfEmpty = async () => {
      try {
        const { count } = await supabase
          .from("shipping_orders")
          .select("*", { count: "exact", head: true })
          .eq("employee_id", user.employeeId);
        if ((count ?? 0) >= 20) return; // already has enough orders, skip

        const fnUrl = `${import.meta.env.VITE_API_URL || ""}/api/functions/seed-existing-employee`;
        const anonKey = getToken() || "";
        const res = await fetch(fnUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${anonKey}` },
          body: JSON.stringify({ email: user.userEmail }),
        });
        if (res.ok) {
          const data = await res.json();
          if ((data as { inserted?: number }).inserted) {
            fetchOrders();
          }
        }
      } catch { /* silent */ }
    };
    seedIfEmpty();
  }, [user.employeeId, user.userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch shipping orders
  const fetchOrders = useCallback(() => {
    setLoading(true);
    setLoadError("");

    supabase
      .from("shipping_orders")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data, error }) => {
        if (error) {
          setLoadError(error.message);
          setOrders([]);
        } else if (data) {
          setOrders((data as unknown[]).map((row) => mapRowToOrder(row as Record<string, unknown>)));
        }
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const availableEmployees = useMemo(() => {
    if (user.isAdmin) return employees;
    return employees.filter((e) => e.id === user.employeeId);
  }, [user.isAdmin, user.employeeId, employees]);

  const filteredOrders = useMemo(() => {
    let result = [...orders];
    if (filter.employeeId !== "all") {
      result = result.filter((o) => o.employeeId === filter.employeeId);
    }
    if (filter.status !== "all") {
      result = result.filter((o) => o.status === filter.status);
    }
    if (filter.search.trim()) {
      const term = filter.search.toLowerCase();
      result = result.filter(
        (o) =>
          o.orderNumber.toLowerCase().includes(term) ||
          o.customer.toLowerCase().includes(term) ||
          o.product.toLowerCase().includes(term) ||
          o.logistics.toLowerCase().includes(term)
      );
    }
    return result;
  }, [orders, filter]);

  const stats: ShippingStats = useMemo(() => {
    const relevant = filter.employeeId === "all" ? orders : orders.filter((o) => o.employeeId === filter.employeeId);
    return {
      total: relevant.length,
      shipping: relevant.filter((o) => o.status === "海運中").length,
      arrived: relevant.filter((o) => o.status === "已到港").length,
      transferred: relevant.filter((o) => o.status === "已轉帳務").length,
    };
  }, [orders, filter.employeeId]);

  // 狀態分頁計數：單一資料源（與 stats 同源），避免「全部」與子分頁數字漂移
  const statusTabCounts: Record<ShippingStatus | "all", number> = useMemo(
    () => ({
      all: stats.total,
      "海運中": stats.shipping,
      "已到港": stats.arrived,
      "已轉帳務": stats.transferred,
    }),
    [stats]
  );

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter.status, filter.search, filter.employeeId]);

  const handleTransfer = useCallback(async (order: ShippingOrder) => {
    if (order.status === "已轉帳務") {
      showToast("此訂單已轉入帳務", "error");
      return;
    }

    // Update shipping order status
    const { error: updateError } = await supabase
      .from("shipping_orders")
      .update({ status: "已轉帳務", updated_at: new Date().toISOString() })
      .eq("id", order.id);

    if (updateError) {
      showToast(`更新失敗：${updateError.message}`, "error");
      return;
    }

    // Create accounting record
    const { error: insertError } = await supabase
      .from("accounting_items")
      .insert({
        order_number: order.orderNumber,
        company: order.customer,
        product: order.product,
        quantity: order.quantity,
        unit: order.unit,
        amount: order.amount,
        currency: order.currency,
        due_date: order.deliveryDeadline,
        transfer_time: new Date().toISOString(),
        payment_status: "未付款",
        assignee: "未指派",
        status: "處理中",
        employee_id: order.employeeId,
        note: "",
      });

    if (insertError) {
      showToast(`轉入帳務失敗：${insertError.message}`, "error");
      // Rollback shipping status
      await supabase.from("shipping_orders").update({ status: order.status, updated_at: new Date().toISOString() }).eq("id", order.id);
      return;
    }

    // Update local state
    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: "已轉帳務" } : o)));
    showToast(`訂單 ${order.orderNumber} 已轉入帳務`);
  }, [showToast]);

  const handleStatusChange = useCallback(async (order: ShippingOrder, newStatus: ShippingStatus) => {
    const { error } = await supabase
      .from("shipping_orders")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", order.id);

    if (error) {
      showToast(`狀態更新失敗：${error.message}`, "error");
      return;
    }

    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status: newStatus } : o)));
    showToast(`訂單 ${order.orderNumber} 狀態已更新為「${newStatus}」`);
  }, [showToast]);

  const handleGenerateDaily = useCallback(async (employeeId?: string) => {
    setGenerating(true);
    try {
      const fnUrl = `${import.meta.env.VITE_API_URL || ""}/api/functions/generate-daily-orders`;
      const anonKey = getToken() || "";
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${anonKey}` },
        body: employeeId ? JSON.stringify({ employee_id: employeeId }) : undefined,
      });
      const data = await res.json();
      if (res.ok && data.success) {
        const msg = `已為 ${data.employeesGenerated} 位員工生成 ${data.totalInserted} 筆，總金額 NT$ ${(data.totalAmount || 0).toLocaleString("zh-TW")}`;
        showToast(msg);
        fetchOrders();
      } else {
        showToast(`生成失敗：${data.error || "未知錯誤"}`, "error");
      }
    } catch {
      showToast("呼叫生成 API 失敗，請稍後再試", "error");
    } finally {
      setGenerating(false);
    }
  }, [fetchOrders, showToast]);

  const handleGenerateSingle = useCallback(() => {
    if (filter.employeeId === "all" || !filter.employeeId) {
      showToast("請先在下方員工篩選器選擇一位員工", "error");
      return;
    }
    handleGenerateDaily(filter.employeeId);
  }, [filter.employeeId, handleGenerateDaily, showToast]);

  const [clearing, setClearing] = useState(false);

  const handleClearToday = useCallback(async () => {
    const isSingle = filter.employeeId !== "all" && !!filter.employeeId;
    const empName = isSingle
      ? employees.find((e) => e.id === filter.employeeId)?.name || "該員工"
      : "";
    const scopeText = isSingle ? `員工「${empName}」的` : "所有員工的";
    if (!window.confirm(`確定要清除${scopeText}全部貨運訂單嗎？\n此操作無法復原。`)) return;

    setClearing(true);
    try {
      const fnUrl = `${import.meta.env.VITE_API_URL || ""}/api/functions/clear-daily-orders`;
      const anonKey = getToken() || "";
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${anonKey}` },
        body: JSON.stringify(isSingle ? { scope: "all", employee_id: filter.employeeId } : { scope: "all" }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        showToast(`已清除${scopeText} ${data.deletedCount} 筆訂單`);
        fetchOrders();
      } else {
        showToast(`清除失敗：${data.error || "未知錯誤"}`, "error");
      }
    } catch {
      showToast("呼叫清除 API 失敗，請稍後再試", "error");
    } finally {
      setClearing(false);
    }
  }, [filter.employeeId, employees, fetchOrders, showToast]);

  const handleRefresh = useCallback(() => {
    fetchOrders();
    showToast("資料已刷新");
  }, [fetchOrders, showToast]);

  const selectedEmployeeName = filter.employeeId === "all"
    ? "全部員工"
    : employees.find((e) => e.id === filter.employeeId)?.name || "";

  return (
    <div className="space-y-5 max-w-[1440px] mx-auto">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 ${
          toast.type === "error" ? "bg-accent-500 text-background-50" : "bg-foreground-800 text-background-50"
        }`}>
          <i className={`${toast.type === "error" ? "ri-error-warning-line" : "ri-check-line"} text-primary-400`} />
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
              貨運追蹤
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {user.isAdmin && (
              <>
                <button
                  onClick={() => handleGenerateDaily()}
                  disabled={generating}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap shrink-0"
                >
                  <i className={`${generating ? "ri-loader-4-line animate-spin" : "ri-rocket-line"}`} />
                  {generating ? "生成中..." : "生成今日訂單（全部）"}
                </button>
                <button
                  onClick={handleGenerateSingle}
                  disabled={generating}
                  title="為下方員工篩選器目前選中的單一位員工生成 20 筆訂單"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-secondary-500 text-background-50 text-sm font-medium hover:bg-secondary-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap shrink-0"
                >
                  <i className={`${generating ? "ri-loader-4-line animate-spin" : "ri-user-add-line"}`} />
                  生成指定員工訂單
                </button>
                <button
                  onClick={handleClearToday}
                  disabled={clearing}
                  title="清除全部貨運訂單（範圍：下方員工篩選器目前的選擇）"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-accent-300 text-accent-600 text-sm font-medium hover:bg-accent-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors whitespace-nowrap shrink-0"
                >
                  <i className={`${clearing ? "ri-loader-4-line animate-spin" : "ri-delete-bin-6-line"}`} />
                  {clearing ? "清除中..." : "清除全部訂單"}
                </button>
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap shrink-0"
                >
                  <i className="ri-add-line" />
                  新增訂單
                </button>
                <button
                  onClick={() => setImportModalOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap shrink-0"
                >
                  <i className="ri-file-excel-2-line" />
                  Excel 匯入
                </button>
              </>
            )}
            <button
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap shrink-0"
            >
              <i className="ri-refresh-line" />
              全部刷新
            </button>
          </div>
        </div>

        {/* Employee Sandbox selector */}
        {user.isAdmin && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-background-200 bg-background-50">
            <i className="ri-user-3-line text-foreground-400 text-sm" />
            <select
              value={filter.employeeId}
              onChange={(e) => setFilter((prev) => ({ ...prev, employeeId: e.target.value }))}
              className="text-sm text-foreground-900 bg-transparent border-none outline-none cursor-pointer min-w-[160px]"
            >
              {user.isAdmin && <option value="all">全部員工</option>}
              {availableEmployees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}（{emp.email}）
                </option>
              ))}
            </select>
          </div>
          <span className="text-xs text-foreground-400">
            {selectedEmployeeName ? `目前檢視：${selectedEmployeeName}` : ""}
          </span>
          {loading && (
            <span className="text-xs text-foreground-400 flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin" />
              載入中...
            </span>
          )}
          {loadError && (
            <span className="text-xs text-accent-600 flex items-center gap-1">
              <i className="ri-error-warning-line" />
              {loadError}
              <button onClick={fetchOrders} className="underline cursor-pointer ml-1">重試</button>
            </span>
          )}
        </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-500">全部訂單</p>
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-file-list-3-line text-foreground-500 text-sm" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground-900 mt-2">{stats.total}</p>
          <p className="text-xs text-foreground-400 mt-0.5">筆</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-500">海運中</p>
            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
              <i className="ri-ship-line text-secondary-500 text-sm" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground-900 mt-2">{stats.shipping}</p>
          <p className="text-xs text-foreground-400 mt-0.5">筆</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground-500">已到港</p>
            <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
              <i className="ri-anchor-line text-primary-500 text-sm" />
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground-900 mt-2">{stats.arrived}</p>
          <p className="text-xs text-foreground-400 mt-0.5">筆</p>
        </div>
      </div>

      {/* Detail Section */}
      <div className="bg-background-50 border border-background-200 rounded-xl">
        <div className="p-4 border-b border-background-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-file-list-3-line text-primary-500" />
                貨運追蹤明細
              </h2>
              <p className="text-xs text-foreground-500 mt-0.5">
                流程：海運中 → 已到港
              </p>
            </div>
            <div className="relative w-full sm:w-64">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
              <input
                type="text"
                value={filter.search}
                onChange={(e) => setFilter((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="搜尋 訂單 / 客戶 / 商品 / 追蹤號"
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
              />
            </div>
          </div>

          {/* Status Tabs */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {statusTabs.map((tab) => {
              const count = statusTabCounts[tab.value] ?? 0;
              return (
                <button
                  key={tab.value}
                  onClick={() => setFilter((prev) => ({ ...prev, status: tab.value }))}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                    filter.status === tab.value
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {tab.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    filter.status === tab.value ? "bg-background-50/20 text-background-50" : "bg-background-200 text-foreground-500"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-50">
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-36">訂單編號</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">客戶</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">商品</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 w-20">數量</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-28">預計到港時間</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-28">訂單交付期限</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 w-28">金額</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500 w-24">狀態</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500 w-28">操作</th>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order) => (
                <tr key={order.id} className="border-b border-background-100 hover:bg-background-50/60 transition-colors">
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-foreground-700">{order.orderNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground-900">{order.customer}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-foreground-700">{order.product}</p>
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-foreground-700 whitespace-nowrap">
                    {order.quantity} {order.unit}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground-600">{order.expectedArrival}</td>
                  <td className="px-4 py-3 text-sm text-foreground-600">{order.deliveryDeadline}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium text-foreground-900 whitespace-nowrap">
                    NT$ {order.amount.toLocaleString("zh-TW")}
                  </td>
                  <td className="px-4 py-3">
                    {order.status === "已轉帳務" ? (
                      <span className={`text-xs font-medium px-2 py-1 rounded-md inline-block ${getStatusColor(order.status)}`}>
                        {order.status}
                      </span>
                    ) : (
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order, e.target.value as ShippingStatus)}
                        className={`text-xs font-medium px-2 py-1 rounded-md border cursor-pointer outline-none transition-colors ${getStatusColor(order.status)}`}
                      >
                        <option value="海運中">海運中</option>
                        <option value="已到港">已到港</option>
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleTransfer(order)}
                      disabled={order.status === "已轉帳務"}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                        order.status === "已轉帳務"
                          ? "bg-background-100 text-foreground-400 cursor-not-allowed"
                          : "bg-primary-500 text-background-50 hover:bg-primary-600"
                      }`}
                    >
                      <i className="ri-arrow-right-circle-line" />
                      轉入帳務
                    </button>
                  </td>
                </tr>
              ))}
              {paginatedOrders.length === 0 && !loading && (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-foreground-400">
                    <i className="ri-inbox-line text-2xl mb-2 block" />
                    找不到符合條件的貨運紀錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-background-200">
          <p className="text-xs text-foreground-500">
            共 {filteredOrders.length} 筆，第 {currentPage} / {totalPages} 頁
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" /> Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page: number;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-7 h-7 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                    currentPage === page
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:bg-background-100"
                  }`}
                >
                  {page}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && (
              <span className="text-xs text-foreground-400 px-1">...</span>
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded-md text-xs text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Next <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      </div>

      <CreateOrderModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => { fetchOrders(); showToast("訂單建立成功"); }}
        isAdmin={user.isAdmin}
        currentEmployeeId={user.employeeId}
      />
      <ExcelImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={() => { fetchOrders(); showToast("匯入完成"); }}
        isAdmin={user.isAdmin}
        currentEmployeeId={user.employeeId}
      />
    </div>
  );
}